/**
 * Accept-time apply for registry record writes (companies, contacts, vehicles,
 * the review queue) proposed by propose-delivery-action. Each runs under the
 * user's JWT through the same RPCs the Registries pages call, so the
 * registry's own access rules hold. propose-delivery-action already read each
 * record and filled every field the upsert replaces, so params are sent as-is.
 * Keep in lockstep with services/shanks/pkg/tools/delivery_action_registry_entities.go.
 */ import { Code, ConnectError } from '@connectrpc/connect';
import { RegistryEntityKind } from '../../../../../../gen/ts/mono/datagateway/v1/registry_resolve_pb';
import { deliveryClient } from '../../../lib/connect/clients';
import { dayToTimestamp } from '../../../pages/Registries/records/company/corporateEvents';
import { registryRecordClient } from '../../../pages/Registries/records/registryRecordClient';
import { registryHref } from '../../../pages/Registries/registryHref';
export const REGISTRY_ENTITY_ACTIONS = [
    'create_company',
    'update_company',
    'add_company_alias',
    'merge_companies',
    'delete_company',
    'update_contact',
    'retire_contact',
    'decide_labor_category_candidates',
    'decide_registry_match_candidate',
    'create_contract_vehicle',
    'update_contract_vehicle',
    'record_corporate_event',
    'update_corporate_event',
    'end_corporate_event',
    'set_company_partner',
    'end_company_partner'
];
export function isRegistryEntityAction(action) {
    return REGISTRY_ENTITY_ACTIONS.includes(action);
}
function str(params, key) {
    const v = params[key];
    return typeof v === 'string' ? v : '';
}
function list(params, key) {
    const v = params[key];
    return Array.isArray(v) ? v.filter((x)=>typeof x === 'string' && x !== '') : [];
}
const REVIEW_QUEUE_HREF = '/registries/review-queue';
/** Where the receipt link opens after a registry write. */ export function registryReceiptHref(action, id) {
    switch(action){
        case 'create_company':
        case 'update_company':
        case 'add_company_alias':
        case 'merge_companies':
            return id && registryHref({
                kind: 'COMPANY',
                id
            }) || '/registries?tab=companies';
        case 'delete_company':
            return '/registries?tab=companies';
        case 'update_contact':
        case 'retire_contact':
            return id && registryHref({
                kind: 'CONTACT',
                id
            }) || '/registries?tab=contacts';
        case 'create_contract_vehicle':
        case 'update_contract_vehicle':
            return id && registryHref({
                kind: 'VEHICLE',
                id
            }) || '/registries?tab=vehicles';
        case 'record_corporate_event':
        case 'update_corporate_event':
        case 'end_corporate_event':
        case 'set_company_partner':
        case 'end_company_partner':
            return id && registryHref({
                kind: 'COMPANY',
                id
            }) || '/registries?tab=companies';
        default:
            return REVIEW_QUEUE_HREF;
    }
}
/** Plain words for the errors a registry write can hit. */ function plainRegistryError(err) {
    if (err instanceof ConnectError) {
        if (err.code === Code.PermissionDenied) {
            return new Error("You can't change the registry. Someone with registries authority on the organization can.");
        }
        if (err.code === Code.NotFound) {
            return new Error("That registry record wasn't found, or it isn't shared with you.");
        }
        if (err.code === Code.AlreadyExists || err.code === Code.FailedPrecondition) {
            return new Error(err.rawMessage || err.message);
        }
    }
    return err;
}
function companyFields(params) {
    return {
        companyId: str(params, 'company_id'),
        name: str(params, 'name'),
        cageCode: str(params, 'cage_code'),
        dunsNumber: str(params, 'duns_number'),
        uei: str(params, 'uei'),
        socioeconomicStatus: str(params, 'socioeconomic_status'),
        website: str(params, 'website'),
        businessSize: str(params, 'business_size'),
        businessSizeSource: str(params, 'business_size_source') || 'MANUAL'
    };
}
function vehicleFields(params) {
    return {
        companyId: str(params, 'company_id'),
        name: str(params, 'name'),
        vehicleType: str(params, 'vehicle_type'),
        contractNumber: str(params, 'contract_number'),
        description: str(params, 'description'),
        agencyAcronym: str(params, 'agency_acronym'),
        status: str(params, 'status') || 'ACTIVE',
        fullName: str(params, 'full_name'),
        orderingEligibility: str(params, 'ordering_eligibility') || 'UNKNOWN'
    };
}
/** The relationship a corporate-event card writes (REG-62); the card filled every field. */ function relationshipFields(params, id) {
    const headcount = params.transferred_headcount;
    return {
        id,
        fromCompanyId: str(params, 'from_company_id'),
        toCompanyId: str(params, 'to_company_id'),
        relationshipType: str(params, 'relationship_type'),
        effectiveStart: dayToTimestamp(str(params, 'effective_date')),
        effectiveEnd: dayToTimestamp(str(params, 'end_date')),
        notes: str(params, 'notes'),
        ...typeof headcount === 'number' ? {
            transferredHeadcount: Math.trunc(headcount)
        } : {},
        transferredHeadcountSource: str(params, 'transferred_headcount_source')
    };
}
/**
 * Decide each suggested labor category in turn. One failure does not undo
 * the ones before it (each decision is its own record), so the error names
 * how many went through.
 */ async function decideLaborCategories(params, opts) {
    const ids = list(params, 'candidate_ids');
    if (ids.length === 0) throw new Error('No suggestions to decide');
    const accept = str(params, 'decision') === 'accept';
    let done = 0;
    for (const id of ids){
        try {
            if (accept) await deliveryClient.acceptLaborCategoryCandidate({
                id
            }, opts);
            else await deliveryClient.rejectLaborCategoryCandidate({
                id
            }, opts);
            done++;
        } catch (err) {
            const why = plainRegistryError(err);
            const msg = why instanceof Error ? why.message : String(why);
            throw new Error(`${done} of ${ids.length} decided; stopped at ${id}: ${msg}`);
        }
    }
}
/** Build and send one registry write. Returns the record id and a receipt link. */ export async function applyRegistryAction(action, params, opts = {}) {
    const done = (id)=>{
        const href = registryReceiptHref(action, id);
        return {
            ...id ? {
                id
            } : {},
            ...href ? {
                href
            } : {}
        };
    };
    try {
        switch(action){
            case 'create_company':
                {
                    const resp = await deliveryClient.upsertCompanyProfile({
                        id: '',
                        ...companyFields(params)
                    }, opts);
                    return done(resp.profile?.id);
                }
            case 'update_company':
                {
                    const id = str(params, 'company_profile_id');
                    if (!id) throw new Error('company_profile_id is required');
                    const resp = await deliveryClient.upsertCompanyProfile({
                        id,
                        ...companyFields(params)
                    }, opts);
                    return done(resp.profile?.id || id);
                }
            case 'add_company_alias':
                {
                    const companyProfileId = str(params, 'company_profile_id');
                    if (!companyProfileId) throw new Error('company_profile_id is required');
                    await deliveryClient.upsertCompanyAlias({
                        companyId: '',
                        alias: {
                            id: '',
                            companyProfileId,
                            aliasName: str(params, 'alias'),
                            aliasType: str(params, 'alias_type') || 'previous'
                        }
                    }, opts);
                    return done(companyProfileId);
                }
            case 'merge_companies':
                {
                    const loserId = str(params, 'duplicate_id');
                    const survivorId = str(params, 'keep_id');
                    if (!loserId || !survivorId) throw new Error('duplicate_id and keep_id are required');
                    const resp = await registryRecordClient.mergeRegistryEntities({
                        kind: RegistryEntityKind.COMPANY,
                        loserId,
                        survivorId,
                        reason: str(params, 'reason') || 'Duplicate registry entry (merged from Specra Assist)'
                    }, opts);
                    return done(resp.survivor?.id || survivorId);
                }
            case 'delete_company':
                {
                    const id = str(params, 'company_profile_id');
                    if (!id) throw new Error('company_profile_id is required');
                    await deliveryClient.deleteCompanyProfile({
                        id
                    }, opts);
                    return done(undefined);
                }
            case 'update_contact':
                {
                    const id = str(params, 'contact_id');
                    if (!id) throw new Error('contact_id is required');
                    // Affiliation, role, user link and labeled emails are left out so the
                    // server keeps what is stored (they are presence-gated on update).
                    const resp = await deliveryClient.upsertContact({
                        id,
                        companyId: str(params, 'company_id'),
                        firstName: str(params, 'first_name'),
                        lastName: str(params, 'last_name'),
                        title: str(params, 'title'),
                        email: str(params, 'email'),
                        phone: str(params, 'phone'),
                        location: str(params, 'location'),
                        organization: str(params, 'organization'),
                        contactType: str(params, 'contact_type'),
                        isActive: true
                    }, opts);
                    return done(resp.contact?.id || id);
                }
            case 'retire_contact':
                {
                    const contactId = str(params, 'contact_id');
                    if (!contactId) throw new Error('contact_id is required');
                    await deliveryClient.retireContact({
                        companyId: str(params, 'company_id'),
                        contactId
                    }, opts);
                    return done(contactId);
                }
            case 'decide_labor_category_candidates':
                await decideLaborCategories(params, opts);
                return done(undefined);
            case 'decide_registry_match_candidate':
                {
                    // expectedRevision 0: the card was built from the row just read.
                    await registryRecordClient.decideRegistryMatchCandidate({
                        candidateId: str(params, 'candidate_id'),
                        decision: str(params, 'decision'),
                        chosenRegistryId: str(params, 'chosen_registry_id'),
                        reason: str(params, 'reason'),
                        expectedRevision: BigInt(0)
                    }, opts);
                    return done(undefined);
                }
            case 'create_contract_vehicle':
                {
                    const resp = await deliveryClient.upsertContractVehicle({
                        id: '',
                        ...vehicleFields(params)
                    }, opts);
                    return done(resp.vehicle?.id);
                }
            case 'update_contract_vehicle':
                {
                    const id = str(params, 'contract_vehicle_id');
                    if (!id) throw new Error('contract_vehicle_id is required');
                    const resp = await deliveryClient.upsertContractVehicle({
                        id,
                        ...vehicleFields(params)
                    }, opts);
                    return done(resp.vehicle?.id || id);
                }
            case 'record_corporate_event':
                {
                    const companyProfileId = str(params, 'company_profile_id');
                    await deliveryClient.createCompanyRelationship({
                        companyId: '',
                        relationship: relationshipFields(params, '')
                    }, opts);
                    return done(companyProfileId || undefined);
                }
            case 'update_corporate_event':
            case 'end_corporate_event':
                {
                    const id = str(params, 'relationship_id');
                    if (!id) throw new Error('relationship_id is required');
                    await deliveryClient.updateCompanyRelationship({
                        companyId: '',
                        relationship: relationshipFields(params, id)
                    }, opts);
                    return done(str(params, 'company_profile_id') || undefined);
                }
            case 'set_company_partner':
                {
                    const companyProfileId = str(params, 'company_profile_id');
                    if (!companyProfileId) throw new Error('company_profile_id is required');
                    await registryRecordClient.createCompanyDesignation({
                        companyId: companyProfileId,
                        kind: 'PARTNER',
                        effectiveStart: dayToTimestamp(str(params, 'effective_date')),
                        notes: str(params, 'notes')
                    }, opts);
                    return done(companyProfileId);
                }
            case 'end_company_partner':
                {
                    const companyProfileId = str(params, 'company_profile_id');
                    if (!companyProfileId) throw new Error('company_profile_id is required');
                    // The open partnership is read at accept time, so a card accepted
                    // later ends whatever partnership is open then.
                    const dossier = await registryRecordClient.getCompanyDossier({
                        companyId: companyProfileId
                    }, opts);
                    const open = dossier.designations.find((d)=>d.kind === 'PARTNER' && !d.effectiveEnd);
                    if (!open) throw new Error(`${dossier.name || 'That company'} isn't marked as a partner.`);
                    await registryRecordClient.updateCompanyDesignation({
                        id: open.id,
                        effectiveStart: open.effectiveStart,
                        effectiveEnd: dayToTimestamp(str(params, 'end_date')),
                        notes: open.notes,
                        sourceArtifactId: open.sourceArtifactId
                    }, opts);
                    return done(companyProfileId);
                }
            default:
                throw new Error(`Unknown registry action ${action}`);
        }
    } catch (err) {
        throw plainRegistryError(err);
    }
}
